import { MemberApi } from '@common/api/member';
import { getClientType } from '@common/application/utils/get-client-type';
import getPhoneStatus from '@common/application/utils/get-phone-status';
import { Api } from '@common/application/utils/request';
import { confirm } from '@common/application/utils/ui/confirm';
import { Button } from '@common/components/common/button';
import { CaptchaButton } from '@common/components/common/captcha-button';
// import { Cookie } from '@common/application/utils/cookie';
import { Checkbox } from '@common/components/common/checkbox';
import { Input } from '@common/components/common/input';
import Select from '@common/components/common/select';
import AreaCodeSelect from '@common/components/common/select/area-code-select';
import { MonthsMap } from '@common/constants/common';
// import { USER_STORAGE_KEY } from '@common/constants/common';
import env from '@common/constants/env';
import { generatePath } from '@common/constants/global-config';
import { LANGUAGE_VALUES } from '@common/constants/language';
import { RegPassword } from '@common/constants/regular';
import type { AreaCode } from '@common/types/checkout';
import type { BaseMemberInfo } from '@common/types/member';
import classNames from 'classnames';
import { useFormik } from 'formik';
import { isNil } from 'lodash-es';
import Router from 'next/router';
import { i18n, useTranslation } from 'next-i18next';
import { useEffect, useMemo, useState } from 'react';
import * as Yup from 'yup';

import { BlackLineIcon } from '@/resources/sprites-images/black-line-icon';
import { ColorFillIcon } from '@/resources/sprites-images/color-fill-icon';

interface Props {
  secondaryRegister?: boolean;
  onSuccess?: () => void;
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export const Register = (props: Props) => {
  const { t } = useTranslation(['common']);
  const [sessionUUID, setSessionUUID] = useState('');
  const [cacheOldPhone, setCacheOldPhone] = useState('');
  /** 手机号是否可注册 */
  const [phoneRegister, setPhoneRegister] = useState(false);
  const [phoneRegisterTips, setPhoneRegisterTips] = useState('');
  const [baseMemberInfo, setBaseMemberInfo] = useState<BaseMemberInfo>();

  const genderOptions = [
    {
      id: '1',
      name: t('appellation.man')
    },
    {
      id: '0',
      name: t('appellation.lady')
    }
  ];

  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const years = Array.from({ length: 101 }, (_, i) => currentYear - 100 + i);
    return years.map((item) => ({
      id: `${item}`,
      name: `${item}${i18n?.language === LANGUAGE_VALUES.EN ? '' : t('year')}`
    }));
  }, []);

  const mouthOptions = useMemo(
    () =>
      Array.from(Array(12 - 1 + 1)).map((_, i) => {
        if (i18n?.language === LANGUAGE_VALUES.EN) {
          return {
            id: `${i + 1}`,
            name: MonthsMap.get(Number(`${i + 1}`))
          };
        }
        return {
          id: `${i + 1}`,
          name: `${i + 1}${t('mouth')}`
        };
      }),
    []
  );

  useEffect(() => {
    MemberApi.getBaseMemberInfo().then(setBaseMemberInfo);
  }, []);

  const navigateToPolicy = () => {
    const href = generatePath(env.SERVICE_POLICY, t('common:footerLink.servicePolicy'));
    if (href) {
      Router.push(href);
    }
  };

  const initialValues = {
    gender: undefined,
    firstName: '',
    lastName: '',
    email: '',
    loginPassword: '',
    loginPasswordAgain: '',
    areaCode: undefined,
    phone: '',
    randomCode: '',
    birthdayYear: undefined,
    birthdayMouth: undefined,
    inviteCode: '',
    isPush: 0
  };
  /**
   * 基础校验
   */
  const validateSchema = {
    gender: Yup.object().required(t('register.gender.emptyMsg')),
    firstName: Yup.string().trim().required(t('register.firstName.emptyMsg')),
    lastName: Yup.string().trim().required(t('register.lastName.emptyMsg')),
    email: Yup.string()
      .trim()
      .required(t('register.email.emptyMsg'))
      .email(t('register.email.rulesMsg'))
  };
  const formik = useFormik<
    Omit<typeof initialValues, 'gender' | 'areaCode' | 'birthdayYear' | 'birthdayMouth'> & {
      gender?: (typeof genderOptions)[0];
      areaCode?: AreaCode;
      birthdayYear?: (typeof yearOptions)[0];
      birthdayMouth?: (typeof mouthOptions)[0];
    }
  >({
    validateOnChange: true,
    validateOnBlur: true,
    initialValues,
    validationSchema: Yup.object().shape({
      ...validateSchema,
      birthdayYear: Yup.object(),
      birthdayMouth: Yup.object().required(t('register.birthdayMouth.emptyMsg')),
      ...(props.secondaryRegister
        ? {}
        : {
            phone: Yup.string()
              .required(t('register.phone.emptyMsg'))
              .matches(/^\d{8}$/, t('register.phone.rulesMsg')),
            randomCode: Yup.string().trim().required(t('register.randomCode.emptyMsg')),
            loginPassword: Yup.string()
              .trim()
              .required(t('register.loginPassword.emptyMsg'))
              .matches(RegPassword, t('register.loginPassword.rulesMsg')),
            loginPasswordAgain: Yup.string()
              .trim()
              .required(t('register.loginPasswordAgain.emptyMsg'))
              .test({
                test(value, ctx) {
                  if (value !== formik.getFieldProps('loginPassword').value) {
                    return ctx.createError({
                      message: t('register.loginPasswordAgain.againMsg')
                    });
                  }
                  return true;
                }
              })
          })
    }),
    onSubmit: async (values: any) => {
      if (!props.secondaryRegister && !phoneRegister) return;
      const birthdayYear = values?.birthdayYear?.id || '';
      const birthdayMouth = values?.birthdayMouth?.id || '';

      let { isPush } = values;
      // 若用户在未勾选广告条款时，点击注册按钮，在其他填写校验通过的前提下，弹出二次确认弹框，再次提醒用户勾选
      if (!isPush) {
        let confirmBtnTrigger = false;
        await confirm({
          className: 'pc:w-100 w-81',
          footerClassName: 'flex flex-col-reverse !pb-5 h-fit pt-0',
          children: (
            <div className="pt-6 text-sm font-medium text-black">
              {t('register.isPush.modalContent')}
            </div>
          ),
          destroyCloseIcon: false,
          closeIcon: <BlackLineIcon icon="x" />,
          confirmText: t('register.isPush.modalConfirm'),
          closeIconClass: '!w-6 !h-6',
          confirmProps: {
            className: '!mx-0 !w-full'
          },
          onConfirm: () => {
            confirmBtnTrigger = true;
            isPush = true;
          },
          onCancel: () => {
            confirmBtnTrigger = true;
            isPush = false;
          },
          onClose: () => {
            confirmBtnTrigger = false;
          },
          cancelProps: {
            className: '!mx-0 !w-full mt-2'
          },
          cancelText: t('register.isPush.modalCancel')
        });
        if (!confirmBtnTrigger) return;
      }
      const params = {
        isPush: isPush ? 1 : 0,
        gender: values.gender.id,
        areaCode: values.areaCode?.code,
        birthday: `${birthdayYear || '1900'}-${
          birthdayMouth < 10 ? `0${Number(birthdayMouth)}` : birthdayMouth
        }-01 00:00:00`,
        inviteCode: values.inviteCode,
        email: values.email,
        firstName: values.firstName,
        lastName: values.lastName,
        loginPassword: values.loginPassword,
        loginPasswordAgain: values.loginPasswordAgain,
        phone: values.phone,
        randomCode: values.randomCode,
        sessionUuid: sessionUUID,
        regSource: getClientType()
      };
      try {
        if (props.secondaryRegister) {
          await MemberApi.secondaryRegister(params);
        } else {
          await MemberApi.register(params);
        }
        // const result: { userSession: string } = await MemberApi.register(params);
        // Cookie.set(null, USER_STORAGE_KEY, result?.userSession);
        // updateUser();
        props.onSuccess?.();
      } catch (e) {
        Api.errorHandle(e);
      }
    }
  });

  const checkPhone = async (areaCodeChange: boolean) => {
    if (formik.errors.phone) return;
    const phone = formik.getFieldProps('phone').value;
    const areaCode = formik.getFieldProps('areaCode').value.code;
    if (!phone || (phone === cacheOldPhone && !areaCodeChange)) return;
    setCacheOldPhone(phone);
    const status = await getPhoneStatus({ areaCode, phone });
    if (status.type === 'notRegister') {
      setPhoneRegisterTips('');
      setPhoneRegister(true);
    } else if (status.type !== 'error') {
      setPhoneRegisterTips(status.message);
      setPhoneRegister(false);
    } else {
      setPhoneRegister(false);
    }
  };
  /** 发送验证码 */
  const sendCaptchaCallBack = (sessionUuid?: string) => {
    if (sessionUuid) {
      setSessionUUID(sessionUuid);
      setPhoneRegisterTips('');
      setPhoneRegister(true);
    } else {
      setPhoneRegisterTips(t('login.phoneStatusTips.abnormal'));
      setPhoneRegister(false);
    }
  };
  useEffect(() => {
    if (formik.getFieldProps('areaCode').value) {
      checkPhone(true);
    }
  }, [formik.getFieldProps('areaCode').value?.code]);

  // 导入的用户自动填充表单
  useEffect(() => {
    if (baseMemberInfo) {
      const initialGender = !isNil(baseMemberInfo.gender)
        ? genderOptions.find((item) => item.id === String(baseMemberInfo.gender))
        : undefined;
      const [year, mouth] = baseMemberInfo.birthday?.split('-') ?? [];
      const initialBirthdayYear = year ? yearOptions.find((item) => item.id === year) : undefined;
      const initialBirthdayMouth = mouth
        ? mouthOptions.find((item) => item.id === mouth.replace(/^0/, ''))
        : undefined;
      // @ts-ignore
      formik.setValues({
        isPush: baseMemberInfo?.isPush,
        gender: initialGender,
        firstName: baseMemberInfo.firstName ?? '',
        lastName: baseMemberInfo.lastName ?? '',
        email: baseMemberInfo.email ?? '',
        birthdayYear: initialBirthdayYear,
        birthdayMouth: initialBirthdayMouth,
        areaCode: { id: '', name: '', code: baseMemberInfo.areaCode },
        inviteCode: baseMemberInfo.inviteCode ?? ''
      });
    }
  }, [baseMemberInfo]);

  return (
    <div className="w-full">
      {baseMemberInfo?.memberType === 2 && (
        <div className="flex pb-2 w-full ">
          <ColorFillIcon className="w-4 h-4" icon="warning" />
          <div className="ml-0.5 text-black-1 text-xs">{t('register.oldMemberTips')}</div>
        </div>
      )}
      <div className="grid gap-3">
        {/* 称谓-性别 */}
        <Select
          required
          className="flex-1"
          name="gender"
          options={genderOptions}
          placeholder={t('appellation.title')}
          value={formik.values.gender}
          onChange={(value) => formik.setFieldValue('gender', value)}
          error={formik.touched.gender ? formik.errors.gender : ''}
        />
        {/* 姓名 */}
        <div className="flex">
          <Input
            valueFromOut
            required
            className="flex-1 mr-3"
            maxLength={20}
            placeholder={t('register.firstName.label')}
            {...formik.getFieldProps('firstName')}
            error={formik.touched.firstName ? formik.errors.firstName : ''}
          />
          <Input
            valueFromOut
            required
            className="flex-1"
            maxLength={20}
            placeholder={t('register.lastName.label')}
            {...formik.getFieldProps('lastName')}
            error={formik.touched.lastName ? formik.errors.lastName : ''}
          />
        </div>
        {/* 郵箱 */}
        <Input
          valueFromOut
          required
          className="flex-1"
          maxLength={100}
          placeholder={t('register.email.label')}
          {...formik.getFieldProps('email')}
          error={formik.touched.email ? formik.errors.email : ''}
        />
        {!props.secondaryRegister && (
          <>
            {/* 手機號 */}
            <div className="flex">
              <AreaCodeSelect
                className="w-19.5"
                selectClassName="border-r-0 rounded-r-none"
                name="areaCode"
                placeholder={t('register.areaCode.label')}
                value={formik.values.areaCode}
                onChange={(value) => formik.setFieldValue('areaCode', value)}
                loadFinish={(result) => {
                  if (!formik.values.areaCode) {
                    formik.setFieldValue('areaCode', result?.[0]);
                  }
                }}
              />
              <Input.Number
                required
                className="flex-1"
                inputClassName="rounded-l-none"
                maxLength={8}
                placeholder={t('register.phone.label')}
                {...formik.getFieldProps('phone')}
                onChange={(value) => {
                  setPhoneRegister(false);
                  setPhoneRegisterTips('');
                  formik.setFieldValue('phone', value);
                }}
                onBlur={(e) => {
                  formik.handleBlur(e);
                  checkPhone(false);
                }}
                error={
                  !phoneRegister && formik.touched.phone
                    ? formik.errors.phone || phoneRegisterTips
                    : ''
                }
              />
            </div>
            {/* 驗證碼 */}
            <div className="flex">
              <Input
                required
                className="flex-1 mr-3"
                maxLength={100}
                placeholder={t('register.randomCode.label')}
                {...formik.getFieldProps('randomCode')}
                error={formik.touched.randomCode ? formik.errors.randomCode : ''}
              />
              <CaptchaButton
                text={t('captchaSend.send')}
                className={classNames('rounded bg-red-light', 'w-24.25 h-11', 'pc:w-21 pc:h-13.5')}
                disabled={!!formik.errors.phone || !phoneRegister}
                callBack={sendCaptchaCallBack}
                areaCode={formik.getFieldProps('areaCode').value.code}
                phone={formik.getFieldProps('phone').value}
              />
            </div>
            {/* 密碼 */}
            <div>
              <Input.Password
                required
                className="flex-1"
                maxLength={16}
                placeholder={t('register.loginPassword.label')}
                {...formik.getFieldProps('loginPassword')}
                error={formik.touched.loginPassword ? formik.errors.loginPassword : ''}
              />
              {!formik.touched.loginPassword ? (
                <div className={'w-full flex items-center text-xs text-blue box-border pt-2'}>
                  <span>{t('register.loginPassword.rulesMsg')}</span>
                </div>
              ) : null}
            </div>
            {/* 確認密碼 */}
            <Input.Password
              required
              className="flex-1"
              maxLength={16}
              placeholder={t('register.loginPasswordAgain.label')}
              {...formik.getFieldProps('loginPasswordAgain')}
              error={formik.touched.loginPasswordAgain ? formik.errors.loginPasswordAgain : ''}
            />
          </>
        )}
        {/* 生日 */}
        <div>
          {/* <div className="text-sm font-medium text-black-1 pb-1 mt-3">
            {t('register.birthdayYear.label')}
            <div className="text-xs text-black-2 pb-2"> {t('register.birthdayYear.extra')}</div>
          </div> */}
          <div className="flex">
            <Select
              required
              className="flex-1 mr-3"
              name="birthdayMouth"
              options={mouthOptions}
              placeholder={t('register.birthdayMouth.placeholder')}
              value={formik.values.birthdayMouth}
              onChange={(value) => formik.setFieldValue('birthdayMouth', value)}
              error={formik.touched.birthdayMouth ? formik.errors.birthdayMouth : ''}
            />
            <Select
              className="flex-1"
              name="birthdayYear"
              options={yearOptions}
              placeholder={t('register.birthdayYear.placeholder')}
              value={formik.values.birthdayYear}
              onChange={(value) => formik.setFieldValue('birthdayYear', value)}
              error={formik.touched.birthdayYear ? formik.errors.birthdayYear : ''}
            />
          </div>
          <div className="text-xs text-black-2 pt-2"> {t('register.birthdayYear.extra')}</div>
        </div>
        {/* 邀请码 */}
        <div className="relative">
          <Input
            valueFromOut
            className="flex-1"
            maxLength={100}
            placeholder={t('register.referee.label')}
            {...formik.getFieldProps('inviteCode')}
          />
        </div>

        {props.secondaryRegister && (
          <div className={classNames('pt-1', 'pc:pt-0')}>
            <Checkbox
              checked={Boolean(formik.values.isPush)}
              onChange={(value) => formik.setFieldValue('isPush', value)}
              iconClassName="!w-4 !h-4"
            >
              <div className="text-xs text-dark-light">{t('register.isPush.startText')}</div>
            </Checkbox>
          </div>
        )}
        {/* 協議 */}
        <div className={classNames('pt-1', 'pc:pt-0')}>
          <div className="text-xs text-dark-light">
            {t('register.clause.startText')}
            <span className="text-primary cursor-pointer" onClick={navigateToPolicy}>
              {t('register.clause.UserAgreement')}
            </span>
            {t('register.clause.dot')}
            <span className="text-primary cursor-pointer" onClick={navigateToPolicy}>
              {t('register.clause.PrivacyAgreement')}
            </span>
            <span className="mx-0.5">{t('register.clause.and')}</span>
            <span className="text-primary cursor-pointer" onClick={navigateToPolicy}>
              {t('register.clause.NoticeAgreement')}
            </span>
          </div>
        </div>
        <div className="pt-2">
          <Button
            type="button"
            className={classNames('flex w-full text-sm text-white bg-primary', 'h-11', 'pc:h-14')}
            onClick={() => formik.handleSubmit()}
          >
            {props.secondaryRegister ? t('register.secendConfirm') : t('register.title')}
          </Button>
        </div>
      </div>
    </div>
  );
};
